import { AccessibleIcon } from '@radix-ui/react-accessible-icon'
import { DialogContentProps, Root } from '@radix-ui/react-dialog'
import { useId } from '@radix-ui/react-id'
import { atom, useAtom, useAtomValue } from 'jotai'
import { Button } from '~/components/button'

import { useDialogState } from '../hooks/use-dialog-state'
import { Overlay } from '../overlay'
import { Body, Close, Container, Content, Footer, Header, Title } from './style'

import type { ComponentType, ReactNode } from 'react'

export const dialogAtom = atom<JSX.Element | null>(null)

export const useDialog = <Props extends {}>(
  Component: ComponentType<Props>,
  intitialProps?: Props
) => {
  const dialogId = useId()
  const [dialog, setDialog] = useAtom(dialogAtom)

  const open = (props: Props) => {
    setDialog(<Component {...intitialProps} {...props} />)
  }

  const buttonProps = {
    'aria-haspopup': 'dialog',
    'aria-expanded': !!dialog,
    'aria-controls': dialog ? dialogId : null,
    // onPress: () => open(props),
  }

  return {
    buttonProps,
    open,
  }
}

interface Props extends DialogContentProps {
  children: ReactNode
  title: string
  cancelLabel?: string
  actionLabel?: string
  onAction: () => void
  onCancel?: () => void
}

const Dialog = (props: Props) => {
  const {
    children,
    title,
    cancelLabel = 'Cancel',
    actionLabel = 'Confirm',
    onAction,
    onCancel,
    ...contentProps
  } = props

  const state = useDialogState()

  return (
    <Root {...state}>
      <Overlay />
      <Content {...contentProps}>
        <Container>
          <Header>
            <Title>{title}</Title>
            <div className="ml-3 h-7 flex items-center">
              <Close>
                <AccessibleIcon label="Close">X</AccessibleIcon>
              </Close>
            </div>
          </Header>
          <Body>
            <div className="border-2 border-dashed border-gray-7 rounded-lg h-96">
              {children}
            </div>
          </Body>
        </Container>
        <Footer>
          <Button onClick={() => state.close()} variant="outline">
            {cancelLabel}
          </Button>
          <Button onClick={onAction}>{actionLabel}</Button>
        </Footer>
      </Content>
    </Root>
  )
}

const DialogsProvider = () => {
  return useAtomValue(dialogAtom)
}

export { Dialog, DialogsProvider }
export type { Props as DialogProps }
